import type { DeliveryBoardDto, DeliveryBoardOrderDto, DeliveryOrderItemDto } from '@matix/api-client';
import { printReport } from '../reports/reportPrint';
import { ORDER_TYPE, STATUS, customerOf, formatStamp, orderNo, pick } from './model';

type Lang = 'en' | 'ar';
export type ReportKind = 'delivery' | 'driver' | 'chef';

/**
 * The three legacy Delivery Manager reports, with the legacy filters:
 *
 *   Delivery report  delivery orders that are New, Ready or out — one block
 *                    per order, sorted by category, no lines
 *   Driver report    the same orders grouped by DRIVER, with lines, each
 *                    order's amount and a total per driver
 *   Chef report      delivery orders not waiting for pickup, grouped by CHEF,
 *                    with lines
 *
 * The legacy page meant the driver and chef reports to list each order's
 * lines but its mapper never loaded them, so they printed empty tables; the
 * lines are fetched here.
 */
export function reportOrders(kind: ReportKind, orders: DeliveryBoardOrderDto[]): DeliveryBoardOrderDto[] {
  const delivery = orders.filter((o) => o.order_type === ORDER_TYPE.Delivery);
  if (kind === 'chef') return delivery.filter((o) => o.status !== STATUS.Waiting);
  return delivery.filter((o) => o.status === STATUS.New || o.status === STATUS.Ready || o.status === STATUS.OnTheWay);
}

export function printDeliveryReport(args: {
  kind: ReportKind;
  board: DeliveryBoardDto;
  items: DeliveryOrderItemDto[];
  branchLabel: string;
  lang: Lang;
  money: (n: number) => string;
}): void {
  const { kind, board, items, branchLabel, lang, money } = args;
  const L = (en: string, ar: string) => esc(lang === 'ar' ? ar : en);
  const branchName = new Map(board.branches.map((b) => [b.id, b.name ?? `#${b.id}`]));
  const linesOf = new Map<number, DeliveryOrderItemDto[]>();
  for (const it of items) linesOf.set(it.order_id, [...(linesOf.get(it.order_id) ?? []), it]);

  let orders = reportOrders(kind, board.orders);
  if (kind === 'delivery') {
    orders = [...orders].sort((a, b) => (a.category ?? '').localeCompare(b.category ?? ''));
  }

  const block = (o: DeliveryBoardOrderDto, withLines: boolean, withAmount: boolean): string => {
    const cat = board.has_categories && o.category ? ` - ${esc(o.category)}` : '';
    const lines = withLines ? linesOf.get(o.id) ?? [] : [];
    const cell = (k: string, v: string) => `<th>${k}</th><td>${v || '&nbsp;'}</td>`;
    return `<div class="dm-o">
      <div class="dm-o-h">${esc(branchName.get(o.branch_id) ?? '')} - ${L('Order', 'طلب')} ${esc(orderNo(o))}${cat}</div>
      <table class="dm-o-t"><tbody>
        <tr>${cell(L('Name', 'الاسم'), esc(customerOf(o, lang)))}${cell(L('Phone', 'الهاتف'), esc(o.customer_phone ?? ''))}</tr>
        <tr>${cell(L('Driver', 'السائق'), esc(pick(lang, o.driver_name, o.driver_name_ar)))}${cell(L('Date', 'التاريخ'), esc(o.delivery_date ? formatStamp(o.delivery_date, lang) : ''))}</tr>
        <tr>${cell(L('Chef', 'الشيف'), esc(pick(lang, o.chef_name, o.chef_name_ar)))}${cell(L('Notes', 'ملاحظات'), esc(o.notes ?? ''))}</tr>
        <tr>${cell(L('Address', 'العنوان'), esc(o.address_details ?? ''))}${withAmount ? cell(L('Amount', 'المبلغ'), `<b class="dm-red">${esc(money(o.amount))}</b>`) : '<th></th><td></td>'}</tr>
      </tbody></table>
      ${lines.length
        ? `<table class="dm-lines"><tbody>${lines
            .map(
              (l) =>
                `<tr><td>${esc(pick(lang, l.name, l.name_ar))}</td><td class="q">${L('Qty', 'الكمية')} = ${fmtQty(l.qty)}</td><td>${esc(l.notes ?? '')}</td></tr>`,
            )
            .join('')}</tbody></table>`
        : ''}
    </div>`;
  };

  const group = (key: (o: DeliveryBoardOrderDto) => string, withAmount: boolean): string => {
    const groups = new Map<string, DeliveryBoardOrderDto[]>();
    for (const o of orders) groups.set(key(o), [...(groups.get(key(o)) ?? []), o]);
    return [...groups.entries()]
      .map(([name, list]) => {
        const total = list.reduce((s, o) => s + o.amount, 0);
        return `<section class="dm-g">
          <h2>${esc(name)}</h2>
          ${list.map((o) => block(o, true, withAmount)).join('')}
          ${withAmount ? `<div class="dm-g-total">${L('Total amount', 'إجمالي المبلغ')}: ${esc(money(total))}</div>` : ''}
        </section>`;
      })
      .join('');
  };

  const unassigned = lang === 'ar' ? 'بدون سائق' : 'No driver';
  const noChef = lang === 'ar' ? 'بدون شيف' : 'No chef';
  const body =
    kind === 'delivery'
      ? orders.map((o) => block(o, false, false)).join('')
      : kind === 'driver'
        ? group((o) => pick(lang, o.driver_name, o.driver_name_ar) || unassigned, true)
        : group((o) => pick(lang, o.chef_name, o.chef_name_ar) || noChef, false);

  const title = {
    delivery: lang === 'ar' ? 'تقرير توصيل الطلبات' : 'Orders delivery report',
    driver: lang === 'ar' ? 'تقرير السائقين' : 'Orders drivers report',
    chef: lang === 'ar' ? 'تقرير الشيف' : 'Orders chef report',
  }[kind];
  const subtitle = `${branchLabel} · ${board.date} · ${orders.length} ${lang === 'ar' ? 'طلب' : 'orders'}`;

  printReport(
    title,
    subtitle,
    `<style>${CSS}</style>${body || `<p class="dm-none">${L('No orders', 'لا توجد طلبات')}</p>`}`,
    'a4',
  );
}

const CSS = `
.dm-o { break-inside: avoid; margin: 0 0 10px; padding: 8px 0; border-bottom: 1px dashed #bbb; }
.dm-o-h { text-align: center; font-weight: 700; font-size: 13px; margin-bottom: 4px; }
.dm-o-t { width: 100%; border-collapse: collapse; font-size: 11px; }
.dm-o-t th { text-align: start; font-weight: 700; white-space: nowrap; padding: 2px 6px 2px 0; width: 1%; }
.dm-o-t td { padding: 2px 10px 2px 0; }
.dm-lines { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 4px; }
.dm-lines td { padding: 2px 6px 2px 0; border-top: 1px solid #eee; }
.dm-lines td.q { white-space: nowrap; width: 1%; }
.dm-g { break-before: auto; margin-bottom: 14px; }
.dm-g h2 { text-align: center; font-size: 15px; margin: 8px 0; padding: 4px 0; border-top: 1px solid #000; border-bottom: 1px solid #000; }
.dm-g-total { text-align: center; color: #b91c1c; font-weight: 700; font-size: 14px; margin-top: 6px; }
.dm-red { color: #b91c1c; }
.dm-none { text-align: center; color: #666; }
`;

function fmtQty(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
